import { Helmet } from "@dr.pogodin/react-helmet";
import ResourceCard from "../components/ResourceCard";

export const meta = {
  slug: "mileage-deduction",
  title: "Mileage Deduction Rules for 1099 Contractors",
  description: "Understand the IRS standard mileage deduction and how to track business miles as a self-employed professional.",
  date: "2025-01-14",
  tags: ["deductions", "mileage", "vehicle expenses"]
};

const otherResources = [
  { title: "Do You Need to Pay Quarterly Taxes?", slug: "do-you-need-to-pay-quarterly-taxes" },
  { title: "Home Office Deductions for 1099 Workers", slug: "home-office" },
  {
    title: "How to Calculate What You Owe as a 1099 Contractor",
    slug: "how-to-calculate-what-you-owe-1099",
  },
  { title: "HSA Deductions for 1099 Workers", slug: "hsa-deductions" },
  { title: "Mileage Deduction Rules for 1099 Contractors", slug: "mileage-deduction" },
  { title: "Who Should Use a 1099 Calculator?", slug: "who-should-use-1099-calculator" },
];

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

export default function MileageDeduction() {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <Helmet>
        <title>Mileage Deduction Rules for 1099 Contractors | Free1099Calc</title>
        <meta
          name="description"
          content="Understand the IRS standard mileage deduction and how to track business miles as a self-employed professional."
        />
      </Helmet>

      <>
        <h1 className="text-3xl font-bold mb-4">Mileage Deduction Rules for 1099 Contractors</h1>
        <p>
          If you drive for business, your mileage can significantly reduce your taxable income. The
          IRS allows a standard deduction per mile or actual vehicle expenses.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-3">The Standard Mileage Rate (2025)</h2>
        <p>
          <strong>67 per mile</strong>. Multiply your business miles by this rate to find your
          deduction.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-3">Tracking Miles Accurately</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>Record odometer start and end of each trip.</li>
          <li>Note the business purpose.</li>
          <li>Use an app or spreadsheet for tracking.</li>
        </ul>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
          <strong>Tip:</strong> Commuting to a regular office is <em>not</em> deductible but client
          visits are.
        </div>
        <hr className="my-8" />
        <h3 className="text-xl font-bold mb-4">Read Other Resources</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {shuffle(otherResources)
            .filter((r) => r.title !== "Mileage Deduction Rules for 1099 Contractors")
            .map((r) => (
              <ResourceCard key={r.slug} title={r.title} link={"/resources/" + r.slug} />
            ))}
        </div>
      </>
    </div>
  );
}
